/**
 * /honorarios/cotizador — Cotizador reactivo F-06.
 *
 * Calcula en vivo mediante POST /api/honorarios/cotizaciones con
 * debounce; muestra desglose completo + totales + proyección INPC
 * + banners de desviación.
 *
 * Botones: Guardar borrador · Enviar propuesta (genera F-06 PDF).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import {
    PRODUCTOS, fmtMXNDec2, BASE_CALCULO,
} from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Slider } from "../../components/ui/slider";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Textarea } from "../../components/ui/textarea";
import { AlertTriangle, FileDown, Save, Send } from "lucide-react";
import { toast } from "sonner";

const DEFAULTS = {
    producto: "P-GAR",
    patrimonio: "150000000",
    flujos: "0",
    slider: 50,              // UI 0-100, se mapea a 0-1
    actos: 4,
    sesiones: 2,
    convenios: 1,
    plazo: 3,
    emisiones: 1,
    proyectar_inpc: true,
    override_activo: false,
    override_mxn: "",
    motivo_desviacion: "",
};

function useDebounced(value, ms = 350) {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return v;
}

function Row({ label, min, max, propuesto, highlight }) {
    return (
        <tr className={highlight ? "bg-amber-50" : ""} data-testid={`row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <td className="px-3 py-2 text-sm">{label}</td>
            <td className="px-3 py-2 text-right text-xs text-muted-foreground">{fmtMXNDec2(min)}</td>
            <td className="px-3 py-2 text-right text-xs text-muted-foreground">{fmtMXNDec2(max)}</td>
            <td className="px-3 py-2 text-right font-semibold text-foreground">{fmtMXNDec2(propuesto)}</td>
        </tr>
    );
}

export default function CotizadorPage() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const solicitudId = params.get("solicitud") || "";
    const [cliente, setCliente] = useState({
        razon_social: "", rfc: "", contacto_nombre: "",
        contacto_email: "", contacto_telefono: "",
    });
    const [form, setForm] = useState(DEFAULTS);
    const [resultado, setResultado] = useState(null);
    const [cotId, setCotId] = useState(null);
    const [folio, setFolio] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const abortRef = useRef(null);

    const debouncedForm = useDebounced(form, 400);

    const canSave = useMemo(
        () => cliente.razon_social && cliente.rfc.length >= 10
              && cliente.contacto_nombre && cliente.contacto_email,
        [cliente],
    );

    // Preview reactivo contra backend (sin persistir) — usamos el motor
    // POST /cotizaciones también sirve, pero evitamos crear borradores por cada
    // tecleo. En su lugar, calculamos localmente llamando a /cotizaciones solo
    // si cambia producto/patrimonio/slider/plazo (debounced), SIN cliente real.
    useEffect(() => {
        if (abortRef.current) abortRef.current.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        setLoading(true);
        const dummyCliente = {
            razon_social: "Preview",
            rfc: "PRE2601010X1",
            contacto_nombre: "Preview",
            contacto_email: "preview@trafalgar.mx",
        };
        api.post("/honorarios/cotizaciones", {
            producto: debouncedForm.producto,
            cliente: dummyCliente,
            patrimonio_mxn: debouncedForm.patrimonio || "0",
            flujos_anuales_mxn: debouncedForm.flujos || "0",
            slider: (debouncedForm.slider / 100).toFixed(2),
            actos_por_anio: parseInt(debouncedForm.actos || 0, 10),
            sesiones_por_anio: parseInt(debouncedForm.sesiones || 0, 10),
            convenios_vida: parseInt(debouncedForm.convenios || 0, 10),
            plazo_anios: parseInt(debouncedForm.plazo || 1, 10),
            emisiones_vivas: parseInt(debouncedForm.emisiones || 1, 10),
            proyectar_inpc: debouncedForm.proyectar_inpc,
            solicitud_pipeline_id: solicitudId || null,
            // Override comercial (sólo cuando toggle activo + motivo >=20 chars)
            ...(debouncedForm.override_activo
                && debouncedForm.override_mxn
                && (debouncedForm.motivo_desviacion || "").length >= 20
                ? {
                    override_admin_anual_propuesto_mxn: debouncedForm.override_mxn,
                    motivo_desviacion: debouncedForm.motivo_desviacion,
                }
                : {}),
        }, { signal: ac.signal })
            .then((r) => {
                setResultado(r.data.resultado);
                // Guardamos id del borrador para posible "Enviar"
                setCotId(r.data.id);
            })
            .catch((e) => {
                if (e.name !== "CanceledError" && e.name !== "AbortError") {
                    toast.error(e?.response?.data?.detail || "Error en cotización");
                }
            })
            .finally(() => setLoading(false));
    }, [debouncedForm, solicitudId]);

    const guardarBorrador = async () => {
        if (!canSave) {
            toast.error("Completa datos del cliente (razón social, RFC, contacto, email).");
            return;
        }
        setSaving(true);
        try {
            const r = await api.post("/honorarios/cotizaciones", {
                producto: form.producto,
                cliente,
                patrimonio_mxn: form.patrimonio || "0",
                flujos_anuales_mxn: form.flujos || "0",
                slider: (form.slider / 100).toFixed(2),
                actos_por_anio: parseInt(form.actos, 10),
                sesiones_por_anio: parseInt(form.sesiones, 10),
                convenios_vida: parseInt(form.convenios, 10),
                plazo_anios: parseInt(form.plazo, 10),
                emisiones_vivas: parseInt(form.emisiones, 10),
                proyectar_inpc: form.proyectar_inpc,
                solicitud_pipeline_id: solicitudId || null,
                ...(form.override_activo && form.override_mxn
                    ? {
                        override_admin_anual_propuesto_mxn: form.override_mxn,
                        motivo_desviacion: form.motivo_desviacion,
                    }
                    : {}),
            });
            setCotId(r.data.id);
            toast.success("Borrador guardado con datos reales del cliente.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error guardando borrador");
        } finally { setSaving(false); }
    };

    const enviar = async () => {
        if (!cotId) { toast.error("Guarda primero el borrador."); return; }
        setSending(true);
        try {
            const r = await api.post(`/honorarios/cotizaciones/${cotId}/enviar`);
            setFolio(r.data.folio);
            toast.success(`Cotización enviada · ${r.data.folio}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error enviando cotización");
        } finally { setSending(false); }
    };

    const descargarF06 = () => {
        if (!cotId) return;
        window.open(
            `${api.defaults.baseURL}/honorarios/cotizaciones/${cotId}/f06.pdf`,
            "_blank",
        );
    };

    const r = resultado;
    const avisoDG = r?.aviso_desviacion?.activa;
    const u = r?.unica_vez || {};
    const rec = r?.recurrente_anual || {};

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="cotizador-page">
            {/* Formulario */}
            <div className="lg:col-span-2 space-y-4">
                <div>
                    <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Cotizador F-06
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Resultado en vivo con cada cambio. Guarda borrador o envía propuesta oficial.
                    </p>
                    {solicitudId && (
                        <Badge className="mt-2 bg-blue-50 text-blue-800 border-blue-200">
                            Vinculada a solicitud F-01: {solicitudId.slice(0, 10)}…
                        </Badge>
                    )}
                </div>

                <Card>
                    <CardHeader><CardTitle className="text-sm">Datos del cliente</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        <div><Label>Razón social</Label>
                            <Input data-testid="cliente-razon-social" value={cliente.razon_social}
                                onChange={(e) => setCliente({ ...cliente, razon_social: e.target.value })} />
                        </div>
                        <div><Label>RFC</Label>
                            <Input data-testid="cliente-rfc" value={cliente.rfc}
                                onChange={(e) => setCliente({ ...cliente, rfc: e.target.value.toUpperCase() })} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Contacto</Label>
                                <Input data-testid="cliente-contacto" value={cliente.contacto_nombre}
                                    onChange={(e) => setCliente({ ...cliente, contacto_nombre: e.target.value })} />
                            </div>
                            <div><Label>Email</Label>
                                <Input data-testid="cliente-email" type="email" value={cliente.contacto_email}
                                    onChange={(e) => setCliente({ ...cliente, contacto_email: e.target.value })} />
                            </div>
                        </div>
                        <div><Label>Teléfono</Label>
                            <Input value={cliente.contacto_telefono}
                                onChange={(e) => setCliente({ ...cliente, contacto_telefono: e.target.value })} />
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader><CardTitle className="text-sm">Parámetros del negocio</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <div><Label>Producto</Label>
                            <Select value={form.producto} onValueChange={(v) => setForm({ ...form, producto: v })}>
                                <SelectTrigger data-testid="select-producto"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {PRODUCTOS.map((p) => (
                                        <SelectItem key={p.code} value={p.code}>
                                            {p.code} · {p.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Patrimonio estimado (MXN)</Label>
                                <Input data-testid="input-patrimonio" value={form.patrimonio}
                                    onChange={(e) => setForm({ ...form, patrimonio: e.target.value.replace(/[^\d]/g, "") })} />
                            </div>
                            <div><Label>Flujos anuales (MXN · sólo P-AFP)</Label>
                                <Input data-testid="input-flujos" value={form.flujos}
                                    onChange={(e) => setForm({ ...form, flujos: e.target.value.replace(/[^\d]/g, "") })} />
                            </div>
                        </div>
                        <div>
                            <Label>Posición en el rango · {form.slider}%</Label>
                            <Slider data-testid="slider-posicion" min={0} max={100} step={1}
                                value={[form.slider]} onValueChange={(v) => setForm({ ...form, slider: v[0] })} />
                            <p className="text-xs text-muted-foreground mt-1">
                                0% = mínimo tarifario · 100% = máximo tarifario · 50% = medio.
                            </p>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            <div><Label>Actos/año</Label><Input type="number" min={0}
                                data-testid="input-actos" value={form.actos}
                                onChange={(e) => setForm({ ...form, actos: e.target.value })} /></div>
                            <div><Label>Sesiones/año</Label><Input type="number" min={0}
                                data-testid="input-sesiones" value={form.sesiones}
                                onChange={(e) => setForm({ ...form, sesiones: e.target.value })} /></div>
                            <div><Label>Convenios</Label><Input type="number" min={0}
                                data-testid="input-convenios" value={form.convenios}
                                onChange={(e) => setForm({ ...form, convenios: e.target.value })} /></div>
                            <div><Label>Plazo (años)</Label><Input type="number" min={1}
                                data-testid="input-plazo" value={form.plazo}
                                onChange={(e) => setForm({ ...form, plazo: e.target.value })} /></div>
                        </div>
                        {form.producto === "P-EMI" && (
                            <div><Label>Emisiones vivas</Label>
                                <Input type="number" min={1} value={form.emisiones}
                                    onChange={(e) => setForm({ ...form, emisiones: e.target.value })} />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Admin anual se multiplica por este número.
                                </p>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <Label htmlFor="inpc-toggle" className="text-sm">
                                Proyectar actualización INPC anual (4.5%)
                            </Label>
                            <Switch id="inpc-toggle"
                                checked={form.proyectar_inpc}
                                onCheckedChange={(v) => setForm({ ...form, proyectar_inpc: v })} />
                        </div>
                    </CardContent>
                </Card>

                <Card data-testid="desviacion-card">
                    <CardHeader>
                        <CardTitle className="text-sm flex items-center justify-between">
                            Desviación comercial (opcional)
                            <Switch
                                data-testid="toggle-desviacion"
                                checked={form.override_activo}
                                onCheckedChange={(v) => setForm({
                                    ...form, override_activo: v,
                                    ...(v ? {} : { override_mxn: "", motivo_desviacion: "" }),
                                })} />
                        </CardTitle>
                    </CardHeader>
                    {form.override_activo && (
                        <CardContent className="space-y-3">
                            <div className="text-xs text-muted-foreground">
                                {r?.recurrente_anual?.administracion_mxn?.min && (
                                    <>Mínimo del tarifario para este patrimonio:{" "}
                                        <b className="text-foreground">
                                            {fmtMXNDec2(r.recurrente_anual.administracion_mxn.min)}
                                        </b>. Si tu propuesta queda bajo este mínimo, la cotización
                                        requerirá aprobación de Dirección General antes de poder
                                        enviarse al cliente.</>
                                )}
                            </div>
                            <div>
                                <Label>Honorario anual propuesto (MXN)</Label>
                                <Input data-testid="input-override-mxn" value={form.override_mxn}
                                    onChange={(e) => setForm({
                                        ...form,
                                        override_mxn: e.target.value.replace(/[^\d.]/g, ""),
                                    })}
                                    placeholder="150000" />
                            </div>
                            <div>
                                <Label>Motivo de la desviación (≥20 caracteres)</Label>
                                <Textarea data-testid="textarea-motivo-desviacion"
                                    rows={3} value={form.motivo_desviacion}
                                    onChange={(e) => setForm({ ...form, motivo_desviacion: e.target.value })}
                                    placeholder="Cliente estratégico Banco X - volumen 500M a 3 años" />
                                <p className={`text-xs mt-1 ${
                                    form.motivo_desviacion.length < 20 ? "text-amber-600" : "text-muted-foreground"
                                }`}>
                                    {form.motivo_desviacion.length}/20 chars mínimo
                                </p>
                            </div>
                        </CardContent>
                    )}
                </Card>

                <div className="flex gap-2">
                    <Button variant="outline" disabled={!canSave || saving} onClick={guardarBorrador}
                        data-testid="btn-guardar-borrador">
                        <Save className="h-4 w-4 mr-2" /> Guardar borrador
                    </Button>
                    <Button disabled={!cotId || sending || avisoDG} onClick={enviar}
                        data-testid="btn-enviar-propuesta"
                        title={avisoDG ? "Primero solicita y obtiene aprobación DG" : ""}
                        className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white">
                        <Send className="h-4 w-4 mr-2" /> Enviar propuesta
                    </Button>
                </div>
                {avisoDG && cotId && (
                    <p className="text-xs text-amber-700">
                        El botón "Enviar" está bloqueado porque la propuesta queda bajo
                        el mínimo tarifario. Abre el detalle de la cotización para
                        solicitar y obtener la aprobación de Dirección General.
                    </p>
                )}
                {folio && (
                    <Alert className="border-emerald-200 bg-emerald-50">
                        <AlertDescription className="flex items-center justify-between">
                            <span>Enviada · <b>{folio}</b></span>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={descargarF06}
                                    data-testid="btn-descargar-f06">
                                    <FileDown className="h-4 w-4 mr-1" /> F-06 PDF
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => nav(`/honorarios/cotizaciones/${folio}`)}>
                                    Ver detalle
                                </Button>
                            </div>
                        </AlertDescription>
                    </Alert>
                )}
            </div>

            {/* Panel resultado en vivo */}
            <div className="lg:col-span-3 space-y-4">
                {avisoDG && (
                    <Alert variant="destructive" data-testid="banner-desviacion">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            Esta cotización está por debajo del mínimo tarifario y requiere
                            aprobación de Dirección General (G-01).
                        </AlertDescription>
                    </Alert>
                )}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Desglose · {form.producto}</CardTitle>
                        {loading && <span className="text-xs text-muted-foreground">Calculando…</span>}
                    </CardHeader>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-xs uppercase">
                                <tr>
                                    <th className="px-3 py-2 text-left">Concepto</th>
                                    <th className="px-3 py-2 text-right">Mínimo</th>
                                    <th className="px-3 py-2 text-right">Máximo</th>
                                    <th className="px-3 py-2 text-right">Propuesto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {r && (<>
                                    <Row label="Estudio (único)" {...u.estudio_mxn} />
                                    <Row label="Aceptación (único)" {...u.aceptacion_mxn} />
                                    <Row label="Admin fiduciaria (anual)" {...rec.administracion_mxn} />
                                    <Row label="Actos extraordinarios (/año)" {...rec.actos_extraordinarios_mxn} />
                                    <Row label="Sesiones adicionales (/año)" {...rec.sesiones_adicionales_mxn} />
                                    <Row label="TOTAL AÑO 1 (sin IVA)" {...r.ano_1_sin_iva} highlight />
                                    <Row label="IVA 16%" {...r.iva_ano_1} />
                                    <Row label="TOTAL AÑO 1 (con IVA)" {...r.ano_1_con_iva} highlight />
                                    <Row label="Recurrente anual año 2+" {...rec.total_mxn} />
                                    <Row label="TOTAL PLAZO (sin IVA)" {...r.total_plazo_sin_iva} highlight />
                                    <Row label="TOTAL PLAZO (con IVA)" {...r.total_plazo_con_iva} highlight />
                                </>)}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>

                {r?.eventuales && (
                    <Card>
                        <CardHeader><CardTitle className="text-sm">Conceptos eventuales (referencia)</CardTitle></CardHeader>
                        <CardContent className="text-sm space-y-2">
                            <p>• Convenio modificatorio: {fmtMXNDec2(r.eventuales.convenio_modificatorio_unitario_mxn?.min)} – {fmtMXNDec2(r.eventuales.convenio_modificatorio_unitario_mxn?.max)} (propuesto {fmtMXNDec2(r.eventuales.convenio_modificatorio_unitario_mxn?.propuesto)})</p>
                            <p>• Sustitución / terminación: {fmtMXNDec2(r.eventuales.sustitucion_mxn?.propuesto)} (0.5 × admin anual)</p>
                            <p className="text-muted-foreground">• Ejecución garantías: {r.eventuales.ejecucion_garantias}</p>
                        </CardContent>
                    </Card>
                )}

                {r?.proyeccion_inpc?.length > 0 && (
                    <Card>
                        <CardHeader><CardTitle className="text-sm">Proyección INPC</CardTitle></CardHeader>
                        <CardContent className="p-0">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40 text-xs uppercase">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Año</th>
                                        <th className="px-3 py-2 text-right">Factor</th>
                                        <th className="px-3 py-2 text-right">Admin proyectada</th>
                                        <th className="px-3 py-2 text-right">Recurrente proyectada</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {r.proyeccion_inpc.map((p) => (
                                        <tr key={p.anio} className="border-t">
                                            <td className="px-3 py-2">Año {p.anio}</td>
                                            <td className="px-3 py-2 text-right">{p.factor}</td>
                                            <td className="px-3 py-2 text-right">{fmtMXNDec2(p.admin_anual_mxn)}</td>
                                            <td className="px-3 py-2 text-right">{fmtMXNDec2(p.recurrente_anual_mxn)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                )}

                {r?.aplicacion_minimos?.length > 0 && (
                    <Alert>
                        <AlertDescription>
                            <b>Reglas aplicadas:</b>
                            <ul className="list-disc list-inside mt-1 text-xs">
                                {r.aplicacion_minimos.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                        </AlertDescription>
                    </Alert>
                )}
            </div>
        </div>
    );
}
